import { setRequestError } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import keycloak from 'services/Keycloak';
import type { AnyObject } from 'types/AnyObject';
import { requestsTitlesMap } from 'types/requestsTitlesMap';

import type { Dispatch } from '@reduxjs/toolkit';
import * as R from 'ramda';

import { DEFAULT_META_KEY, ERROR_403_PATH, PATH_BY_REQUEST_CODE } from './constants';

interface CommonPayloadInterface {
    message: string;
    validation: { message: string }[];
    result: boolean;
    data: AnyObject;
    buttonText?: string;
    hideValidation?: boolean;
    hasValidation: boolean;
}

type CommonPayloadType = { dependenciesUpdater?: () => void; onErrorUpdater?: (...args: any) => void } & (
    | { [key: string]: CommonPayloadInterface }
    | { [key: string]: CommonPayloadInterface }[]
);

interface IActionCommonInterface {
    type: string;
    payload: CommonPayloadType | string;
    meta: {
        reducerId: string;
    };
}

interface IMethodInterface<T> {
    type: string;
    payload: T;
    dispatch: Dispatch<any>;
    meta: {
        reducerId: string;
    };
    getState?: any;
}

type KeyofRequestsTitlesMap = keyof typeof requestsTitlesMap;

type CheckMetaStopList = Pick<IMethodInterface<string>, 'meta' | 'getState'>;

const DEFAULT_TITLE = 'Что-то пошло не так';
const DEFAULT_ERR = [{ message: 'Попробуйте обновить страницу или зайдите позже' }];
const DEFAULT_ERR_EXTENDED = [{ message: `${DEFAULT_TITLE}. Попробуйте обновить страницу или зайдите позже` }];

class ErrorHandler {
    private readonly actionMap: Record<string, boolean> = { 'request/setError': true, 'request/setData': true };

    private readonly payloadKeysForExtraction = [
        'message',
        'data',
        'result',
        'validation',
        'buttonText',
        'hideValidation',
        'hasValidation',
    ];

    private readonly getFlattenValueArr: (obj: any) => CommonPayloadInterface[] = R.pipe(R.values, R.flatten);

    private readonly getObjectValues: (arr: readonly any[]) => CommonPayloadInterface[] = R.map<
        any,
        CommonPayloadInterface
    >(R.pickAll(this.payloadKeysForExtraction));

    private readonly dependenciesUpdater: Partial<Record<string, Function>> = {};

    private readonly onErrorUpdater: Partial<Record<string, Function>> = {};

    private readonly stopList = {
        AUTHENTICATED_ERROR: 'AUTH_NOT_AUTHENTICATED',
        AUTHORIZED_ERROR: 'AUTH_NOT_AUTHORIZED',
        EXCEPTION: 'An exception has been raised',
    };

    private checkPayloadAndGetValue(payload: CommonPayloadType) {
        const flattenPayloadArr = this.getFlattenValueArr(payload);

        if (flattenPayloadArr.length === 1 && flattenPayloadArr[0]) {
            const errorObject = this.getObjectValues(flattenPayloadArr)[0];
            const { message, validation, result, data } = errorObject;

            // Если при скачивании файла было переданно информационное сообщение, то оно не блокирует dependenciesUpdater
            if (result && data?.dataUrl) {
                return null;
            }

            return message || validation ? [{ message: message || 'Ошибка запроса' }, ...(validation || [])] : null;
        }

        return null;
    }

    private getValueRequestData(payload: CommonPayloadType) {
        const flattenPayloadArr = this.getFlattenValueArr(payload);

        if (flattenPayloadArr.length === 1 && flattenPayloadArr[0]) {
            return this.getObjectValues(flattenPayloadArr)[0];
        }

        return null;
    }

    private static dispatchError(
        checkStopListResult: string,
        dispatch: Dispatch<any>,
        meta: { reducerId: string },
        errorObject: any,
        requestData?: CommonPayloadInterface | null,
    ) {
        dispatch(
            setRequestError({
                id: meta[DEFAULT_META_KEY],
                error: errorObject,
                status: RequestStatus.ERROR,
                title: checkStopListResult,
                data: requestData,
            }),
        );
    }

    private stringPayloadReceived({ type, payload, meta, dispatch, getState }: IMethodInterface<string>) {
        if (payload.includes(this.stopList.AUTHORIZED_ERROR)) {
            setTimeout(() => {
                window.location.href = ERROR_403_PATH;
            }, 500);

            return;
        }

        if (payload.includes(this.stopList.AUTHENTICATED_ERROR)) {
            keycloak.logout();

            return;
        }

        const requestCode = getState()?.request?.code;
        const path = PATH_BY_REQUEST_CODE[requestCode];

        if (path) {
            setTimeout(() => {
                window.location.href = path;
            }, 100);

            return;
        }

        if (this.actionMap[type]) {
            const title = checkMetaStopList({ meta, getState });

            ErrorHandler.dispatchError(
                title ?? DEFAULT_TITLE,
                dispatch,
                meta,
                title ? DEFAULT_ERR_EXTENDED : DEFAULT_ERR,
            );

            this.dependenciesUpdater[meta[DEFAULT_META_KEY]] = undefined;
            this.onErrorUpdater[meta?.[DEFAULT_META_KEY]] = undefined;
        }
    }

    private objectPayloadReceived({ type, meta, payload, dispatch, getState }: IMethodInterface<CommonPayloadType>) {
        const errorObject = this.checkPayloadAndGetValue(payload);
        const title = checkMetaStopList({ meta, getState });
        const requestData = this.getValueRequestData(payload);

        const { dependenciesUpdater, onErrorUpdater } = payload;

        if (dependenciesUpdater && meta?.[DEFAULT_META_KEY]) {
            this.dependenciesUpdater[meta[DEFAULT_META_KEY]] = dependenciesUpdater;
        }

        if (onErrorUpdater && meta?.[DEFAULT_META_KEY]) {
            this.onErrorUpdater[meta[DEFAULT_META_KEY]] = onErrorUpdater;
        }

        if (this.actionMap[type] && title && errorObject && requestData) {
            ErrorHandler.dispatchError(title, dispatch, meta, errorObject, requestData);

            if (this.onErrorUpdater[meta?.[DEFAULT_META_KEY]]) {
                setTimeout(() => {
                    this.onErrorUpdater[meta[DEFAULT_META_KEY]]?.({
                        reducerId: meta[DEFAULT_META_KEY],
                        payload,
                        variables: getState()?.request?.variables,
                    });
                    this.onErrorUpdater[meta?.[DEFAULT_META_KEY]] = undefined;
                }, 500);
            }
        }

        if (!errorObject && this.dependenciesUpdater[meta?.[DEFAULT_META_KEY]] && this.actionMap[type]) {
            setTimeout(() => {
                this.dependenciesUpdater[meta[DEFAULT_META_KEY]]?.({
                    reducerId: meta[DEFAULT_META_KEY],
                    payload,
                    variables: getState()?.request?.variables,
                });
                this.dependenciesUpdater[meta[DEFAULT_META_KEY]] = undefined;
            }, 500);
        }
    }

    public checkAction(action: IActionCommonInterface, dispatch: Dispatch<any>, getState: any): void {
        const { payload, type, meta } = action;

        if (typeof payload === 'string') {
            this.stringPayloadReceived({ type, payload, meta, dispatch, getState });
        }

        if (typeof payload !== 'string' && payload === Object(payload)) {
            this.objectPayloadReceived({ type, payload, meta, dispatch, getState });
        }
    }
}

const checkMetaStopList = ({ meta }: CheckMetaStopList): string | undefined => {
    if (meta?.[DEFAULT_META_KEY]?.includes('_')) {
        const splitMeta = meta[DEFAULT_META_KEY].split('_');
        const requestId = splitMeta[0] as KeyofRequestsTitlesMap;

        return requestsTitlesMap[requestId];
    }

    return requestsTitlesMap[meta?.[DEFAULT_META_KEY] as KeyofRequestsTitlesMap];
};

const onError = new ErrorHandler();

export const errors =
    ({ dispatch, getState }: { dispatch: Dispatch<any>; getState: any }) =>
    (next: Dispatch) =>
    (action: IActionCommonInterface) => {
        onError.checkAction(action, dispatch, getState);

        return next(action);
    };
